import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { HOLDER_ROLE, normalizeJoinCode, type MyInvitation, type PracticeType, type RecordHolder } from '@curentia/shared';
import { ErrorText, Field, LockButton, Page, Submit } from '../components';
import {
  PRACTICE_TYPE_BLURBS,
  PRACTICE_TYPE_LABELS,
  STATUS_LABELS,
  foundableTypes,
  roleList,
  worksInPractices,
} from '../lib/practices';
import { useLoad } from '../lib/useLoad';
import { useAction, useDevice, useSnapshot } from '../state';

/** The practices card on the home screen of doctors, pharmacists and clinic staff. */
export function PracticesCard() {
  const device = useDevice();
  const { account, practicesVersion } = useSnapshot();
  const member = worksInPractices(account?.type);
  const { data, error } = useLoad(async () => {
    if (!member) return null;
    const [{ practices }, { invitations }] = await Promise.all([device.api.practices(), device.api.myInvitations()]);
    return { practices, invitations };
  }, [device, member, practicesVersion]);
  if (!account || !member) return null;
  return (
    <section className="card" aria-labelledby="practices-heading">
      <h2 id="practices-heading">Your practices</h2>
      <ErrorText error={error} />
      {!data && !error ? <p className="muted">Loading…</p> : null}
      {data && data.practices.length === 0 ? <p className="muted">You are not part of a practice yet.</p> : null}
      {data && data.practices.length > 0 ? (
        <ul className="list">
          {data.practices.map(({ practice, roles }) => (
            <li key={practice.id}>
              <Link to={`/practices/${practice.id}`} className="list-link">
                <strong>{practice.name}</strong>
                <span className="muted">
                  {PRACTICE_TYPE_LABELS[practice.type]} · {practice.city} · {roleList(roles)}
                </span>
                {practice.status === 'active' ? null : <span className={`badge ${practice.status}`}>{STATUS_LABELS[practice.status]}</span>}
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
      {data?.invitations.map((invitation) => <WaitingToJoin key={invitation.id} invitation={invitation} />)}
      <div className="actions">
        {foundableTypes(account.type).length > 0 ? (
          <Link to="/practices/new" className="button small">
            Set up a practice
          </Link>
        ) : null}
        <Link to="/join" className="button secondary small">
          Join with a code
        </Link>
      </div>
    </section>
  );
}

function WaitingToJoin({ invitation }: { invitation: MyInvitation }) {
  return (
    <p className="note" role="status">
      You asked to join {invitation.practice.name} as {roleList(invitation.roles).toLowerCase()}. Its practice admin will approve you.
    </p>
  );
}

const REGISTRATION_LABELS: Record<Exclude<PracticeType, 'solo'>, { authority: string; number: string; hint: string }> = {
  clinic: { authority: 'Registered with', number: 'Registration number', hint: 'For example, the state’s clinical establishments authority' },
  small_hospital: { authority: 'Registered with', number: 'Registration number', hint: 'For example, the state’s clinical establishments authority' },
  pharmacy: { authority: 'Drug licence issued by', number: 'Drug licence number', hint: 'For example, the state drugs controller' },
};

export function NewPractice() {
  const device = useDevice();
  const navigate = useNavigate();
  const { account } = useSnapshot();
  const types = account ? foundableTypes(account.type) : [];
  const [type, setType] = useState<PracticeType | null>(types.length === 1 ? (types[0] ?? null) : null);
  const [form, setForm] = useState({ name: '', city: '', authority: '', number: '' });
  const [holder, setHolder] = useState<RecordHolder>('device');
  const { busy, error, setError, run } = useAction();
  const set = (key: keyof typeof form) => (event: { target: { value: string } }) => setForm({ ...form, [key]: event.target.value });
  const registration = type && type !== 'solo' ? REGISTRATION_LABELS[type] : null;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!type) return setError('Choose the kind of practice.');
    if (form.name.trim().length < 2) return setError('Enter the practice name.');
    if (form.city.trim().length < 2) return setError('Enter the city.');
    if (registration && (!form.authority.trim() || !form.number.trim())) return setError(`Enter the ${registration.number.toLowerCase()} and who issued it.`);
    void run(async () => {
      const { practice, roles } = await device.api.createPractice({
        name: form.name.trim(),
        type,
        city: form.city.trim(),
        ...(registration ? { registration: { authority: form.authority.trim(), number: form.number.trim() } } : {}),
        ...(type === 'pharmacy' ? { recordHolder: holder } : {}),
      });
      // A solo doctor or a pharmacist keeps the records on this device from the start.
      if (practice.recordHolder === 'device' && roles.includes(HOLDER_ROLE[practice.type])) await device.holdPracticeRecords(practice.id);
      navigate(`/practices/${practice.id}`);
    });
  };

  return (
    <Page actions={<LockButton />}>
      <h1>Set up a practice</h1>
      <form onSubmit={submit} noValidate>
        <fieldset className="choices">
          <legend>Kind of practice</legend>
          {types.map((option) => (
            <label key={option} className="choice">
              <input type="radio" name="type" value={option} checked={type === option} onChange={() => setType(option)} />
              <span>
                <strong>{PRACTICE_TYPE_LABELS[option]}</strong>
                <span className="muted">{PRACTICE_TYPE_BLURBS[option]}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <Field label="Practice name" value={form.name} onChange={set('name')} required />
        <Field label="City" autoComplete="address-level2" value={form.city} onChange={set('city')} required />
        {registration ? (
          <>
            <Field label={registration.authority} value={form.authority} onChange={set('authority')} hint={registration.hint} required />
            <Field label={registration.number} value={form.number} onChange={set('number')} hint="Our verification team checks this before records can flow." required />
          </>
        ) : null}
        {type === 'pharmacy' ? (
          <fieldset className="choices">
            <legend>Where the pharmacy’s records are kept</legend>
            <label className="choice">
              <input type="radio" name="holder" checked={holder === 'device'} onChange={() => setHolder('device')} />
              <span>
                <strong>On a pharmacist’s device</strong>
                <span className="muted">For a pharmacy with one counter.</span>
              </span>
            </label>
            <label className="choice">
              <input type="radio" name="holder" checked={holder === 'hub'} onChange={() => setHolder('hub')} />
              <span>
                <strong>On a hub PC</strong>
                <span className="muted">For a pharmacy with several counters.</span>
              </span>
            </label>
          </fieldset>
        ) : null}
        {type && type !== 'solo' ? <p className="muted">Clinics, hospitals and pharmacies are verified by our team before records can flow. You can invite your team meanwhile.</p> : null}
        <ErrorText error={error} />
        <Submit busy={busy} busyText="Setting up…">
          Set up the practice
        </Submit>
      </form>
      <div className="links">
        <Link to="/home">Back</Link>
      </div>
    </Page>
  );
}

export function Join() {
  const device = useDevice();
  const [code, setCode] = useState('');
  const [joined, setJoined] = useState<MyInvitation | null>(null);
  const { busy, error, setError, run } = useAction();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!normalizeJoinCode(code)) return setError('Enter the 10-character code from the practice.');
    void run(async () => {
      const { invitation } = await device.api.acceptInvitation(code);
      setJoined(invitation);
    });
  };

  return (
    <Page actions={<LockButton />}>
      <h1>Join a practice</h1>
      {joined ? (
        <section className="card tinted">
          <h2>Request sent</h2>
          <p>
            You asked to join {joined.practice.name} ({PRACTICE_TYPE_LABELS[joined.practice.type]}, {joined.practice.city}) as{' '}
            {roleList(joined.roles).toLowerCase()}.
          </p>
          <p className="muted">Its practice admin will approve you. The practice then appears on your home screen.</p>
        </section>
      ) : (
        <form onSubmit={submit} noValidate>
          <p className="muted">Enter the code the practice admin gave you. It works once.</p>
          <Field label="Invitation code" autoComplete="off" autoCapitalize="characters" value={code} onChange={(e) => setCode(e.target.value)} required />
          <ErrorText error={error} />
          <Submit busy={busy} busyText="Joining…">
            Join
          </Submit>
        </form>
      )}
      <div className="links">
        <Link to="/home">Back</Link>
      </div>
    </Page>
  );
}
